/**
 * Resolve the API base URL.
 *
 * Order of precedence:
 *  1. NEXT_PUBLIC_API_URL set at build time (including empty string for relative URLs).
 *  2. In the browser on a non-dev host: empty string → relative URLs (proxied by the
 *     same origin, e.g. Nginx → API). This means a tunnel/host swap doesn't require
 *     a rebuild.
 *  3. Dev fallback: http://localhost:4000 (prod-port host) or :4100 (dev-port host).
 */
export function getApiUrl(): string {
  const fromEnv = process.env.NEXT_PUBLIC_API_URL;
  if (fromEnv !== undefined) return fromEnv;
  if (typeof window !== 'undefined') {
    const host = window.location.host;
    const isProdLocalHost = host === 'localhost:3000' || host.startsWith('127.0.0.1:3000');
    const isDevLocalHost = host === 'localhost:3002' || host.startsWith('127.0.0.1:3002');
    if (isProdLocalHost) {
      return 'http://localhost:4000';
    }
    if (isDevLocalHost) {
      return 'http://localhost:4100';
    }
    return '';
  }
  return 'http://localhost:4000';
}

const API_URL = getApiUrl();

interface ApiError {
  error: string;
  details?: { field: string; message: string }[];
  code?: string;
}

class ApiClient {
  private accessToken: string | null = null;
  private hasRefreshCookie = false;
  private refreshPromise: Promise<boolean> | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      this.accessToken = localStorage.getItem('access_token');
      this.hasRefreshCookie = localStorage.getItem('has_refresh') === '1';
    }
  }

  setTokens(access: string) {
    this.accessToken = access;
    this.hasRefreshCookie = true;
    localStorage.setItem('access_token', access);
    localStorage.setItem('has_refresh', '1');
  }

  clearTokens() {
    this.accessToken = null;
    this.hasRefreshCookie = false;
    localStorage.removeItem('access_token');
    localStorage.removeItem('has_refresh');
  }

  get isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  private async doRefresh(): Promise<boolean> {
    if (!this.hasRefreshCookie) return false;
    try {
      const res = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: '{}',
      });
      if (!res.ok) {
        // ONLY a rejection of the token itself ends the session.
        //
        // This used to clear on any non-2xx, so a 429 from the auth rate
        // limiter or a 502 during a deploy wiped the operator's tokens and
        // dropped them to the login screen mid-flight - and the 429 case is
        // self-inflicting, because every queued request retries the refresh and
        // drives the limiter further into the bucket that caused it.
        //
        // 429 and 5xx say nothing about whether the session is valid. The
        // httpOnly refresh cookie is still good server-side, so keeping the
        // tokens lets the next call succeed once the condition clears. Same
        // rule `isAuthRejection` already applies in useAuth.
        if (res.status === 401 || res.status === 403) this.clearTokens();
        return false;
      }
      const data = await res.json();
      this.setTokens(data.access_token);
      return true;
    } catch {
      // A network blip is not a logout either - offline, DNS, a dropped TLS
      // handshake. Nothing here is evidence about the session.
      return false;
    }
  }

  private async tryRefresh(): Promise<boolean> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.doRefresh().finally(() => {
        this.refreshPromise = null;
      });
    }
    return this.refreshPromise;
  }

  async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    let res = await fetch(`${API_URL}${path}`, { ...options, headers });

    if (res.status === 401 && this.hasRefreshCookie) {
      const refreshed = await this.tryRefresh();
      if (refreshed) {
        headers['Authorization'] = `Bearer ${this.accessToken}`;
        res = await fetch(`${API_URL}${path}`, { ...options, headers });
      }
    }

    if (!res.ok) {
      const body: ApiError = await res.json().catch(() => ({ error: 'Request failed' }));
      throw new ApiRequestError(res.status, body.error, body.details, body.code);
    }

    return res.json();
  }

  /** Binary download (e.g. PDF); does not parse JSON on success. */
  async downloadBlob(path: string): Promise<Blob> {
    const headers: Record<string, string> = {};
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    let res = await fetch(`${API_URL}${path}`, { headers });

    if (res.status === 401 && this.hasRefreshCookie) {
      const refreshed = await this.tryRefresh();
      if (refreshed) {
        headers['Authorization'] = `Bearer ${this.accessToken}`;
        res = await fetch(`${API_URL}${path}`, { headers });
      }
    }

    if (!res.ok) {
      const body: ApiError = await res.json().catch(() => ({ error: 'Request failed' }));
      throw new ApiRequestError(res.status, body.error, body.details, body.code);
    }

    return res.blob();
  }

  /** `options` carries an AbortSignal for callers that need a timeout or
   *  cancellation — polling hooks must never leave a request hanging forever. */
  get<T>(path: string, options?: RequestInit) {
    return this.request<T>(path, options);
  }

  /**
   * `options.keepalive` lets a POST outlive the document that started it.
   *
   * For the one case that needs it: a page being closed or reloaded while it
   * holds something the server must be told about — manual flight authority.
   * A plain fetch issued from `beforeunload` is cancelled with the document, so
   * the release never lands and the aircraft stays locked until the RC's own
   * dead-man expires. Bodies are capped at 64 KB by the spec, which every
   * caller of this is far inside.
   *
   * `options.signal` is the same contract as `get`/`put`: a caller that must
   * not hang forever supplies its own AbortSignal — a command button awaiting
   * this promise stays wedged (and looks armed) for exactly as long as the
   * request does.
   *
   * `options.timeoutMs` bounds the request without the caller owning a
   * controller: past it the fetch is aborted and the promise rejects with a
   * `TimeoutError`. OPT-IN, and deliberately so — the right bound is the
   * caller's business, and a POST that creates something has no safe one. It
   * exists for a caller that REPLACES its own request on a fixed cadence: a
   * manual-flight setpoint superseded 320 ms later, a heartbeat superseded
   * every second. Without it a stalled TCP path lets those accumulate for
   * ever, and neither their success nor their failure handler ever runs —
   * which is a console rendering LIVE over a lock nothing renews.
   *
   * Passing both wins for `timeoutMs`: an explicit bound is the stricter
   * promise to the caller, and the two abort mechanisms cannot be merged
   * without a controller this layer has no reason to own.
   */
  post<T>(
    path: string,
    body: unknown,
    options: Pick<RequestInit, 'signal'> & { keepalive?: boolean; timeoutMs?: number } = {},
  ) {
    return this.request<T>(path, {
      method: 'POST',
      body: JSON.stringify(body),
      ...(options.keepalive ? { keepalive: true } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
      // `request` spreads its options into `fetch`, so the signal needs no
      // handling of its own there. The 401 retry re-uses this same signal on
      // purpose: the timeout budgets the whole call, not each attempt.
      ...(options.timeoutMs === undefined
        ? {}
        : { signal: AbortSignal.timeout(options.timeoutMs) }),
    });
  }

  /** Same contract as `post`: `options` carries an AbortSignal for callers that must not hang. */
  put<T>(path: string, body: unknown, options?: Pick<RequestInit, 'signal'>) {
    return this.request<T>(path, { method: 'PUT', body: JSON.stringify(body), ...options });
  }

  patch<T>(path: string, body: unknown) {
    return this.request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
  }

  /**
   * Same contract as `post` for the two things that keep a command button from wedging.
   *
   * It took no options at all, so a caller awaiting it could not bound it - and the COP's detach
   * is one: a stalled DELETE left every control on the coverage block dead behind a `submitting`
   * that never cleared, on the surface flying aircraft.
   */
  delete<T>(
    path: string,
    options: Pick<RequestInit, 'signal'> & { timeoutMs?: number } = {},
  ) {
    return this.request<T>(path, {
      method: 'DELETE',
      ...(options.signal ? { signal: options.signal } : {}),
      ...(options.timeoutMs === undefined
        ? {}
        : { signal: AbortSignal.timeout(options.timeoutMs) }),
    });
  }
}

export function getApiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiRequestError) {
    if (err.status === 403) return 'Permission denied - your role does not allow this action';
    if (err.status === 401) return 'Session expired - please log in again';
    return err.message || fallback;
  }
  return fallback;
}

export class ApiRequestError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: { field: string; message: string }[],
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

export const api = new ApiClient();
